/**
 * SIM-SZENE LEIPZIG – Sound (90er-Sounds)
 * Lightweight: SFX einmal beim Start (nach User-Geste) decodieren, danach aus Buffer abspielen (keine erneuten Netz-/Decode-Lasten).
 * Mute aus localStorage; BGM pro Phase (Pool pro Phase, streaming).
 */
import { BGM_BASE, BGM_POOL_BY_PHASE } from './bgmConfig.js';

const STORAGE_KEY = 'sim-szene-muted';
const MUSIC_MUTED_KEY = 'sim-szene-music-muted';
const MUSIC_VOLUME_KEY = 'sim-szene-music-volume';
const DEFAULT_MUSIC_VOLUME = 0.25; // moderat leise (0–1)
const BASE = '/assets/audio';
const EXTENSIONS = ['.ogg', '.wav'];

let audioContext = null;
/** @type {Record<string, AudioBuffer>} */
const buffers = {};
let preloadPromise = null;

function getAudioContext() {
  if (!audioContext) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) audioContext = new Ctx();
  }
  return audioContext;
}

function getMuted() {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setMuted(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value ? 'true' : 'false');
  } catch (_) {}
}

// --- Musik (BGM) separat: an/aus (Default: an), Lautstärke (Default: moderat leise) ---
function parseMusicVolume(val) {
  const n = Number(val);
  if (Number.isFinite(n) && n >= 0 && n <= 1) return n;
  return DEFAULT_MUSIC_VOLUME;
}

export function getMusicMuted() {
  try {
    return localStorage.getItem(MUSIC_MUTED_KEY) === 'true';
  } catch {
    return false;
  }
}

export function setMusicMuted(value) {
  try {
    localStorage.setItem(MUSIC_MUTED_KEY, value ? 'true' : 'false');
  } catch (_) {}
  if (bgmEl) {
    if (value) bgmEl.pause();
    else if (currentBGMPhase != null && bgmEl.src) {
      bgmEl.volume = getMusicVolume();
      bgmEl.play().catch(() => {});
    }
  }
}

export function getMusicVolume() {
  try {
    const v = localStorage.getItem(MUSIC_VOLUME_KEY);
    return v != null ? parseMusicVolume(parseFloat(v)) : DEFAULT_MUSIC_VOLUME;
  } catch {
    return DEFAULT_MUSIC_VOLUME;
  }
}

export function setMusicVolume(value) {
  const vol = parseMusicVolume(value);
  try {
    localStorage.setItem(MUSIC_VOLUME_KEY, String(vol));
  } catch (_) {}
  if (bgmEl) bgmEl.volume = vol;
}

/** Kurzer Beep per Web Audio API (Fallback wenn Datei fehlt / noch nicht geladen). */
function playFallbackBeep(frequency = 800, durationMs = 40) {
  try {
    const ctx = getAudioContext();
    if (!ctx) return;
    const play = () => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = frequency;
      osc.type = 'sine';
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + durationMs / 1000);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + durationMs / 1000);
    };
    if (ctx.state === 'suspended') ctx.resume().then(play).catch(() => {});
    else play();
  } catch (_) {}
}

function playFromBuffer(buffer) {
  const ctx = getAudioContext();
  if (!ctx || !buffer) return;
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(ctx.destination);
  src.start(0);
}

/**
 * Lädt alle SFX einmal, decodiert in AudioBuffers. Nach User-Geste aufrufen (z. B. „Spiel starten“).
 * Danach sind Klick/GameOver/NewScenario sofort abspielbar ohne erneutes Laden.
 * @returns {Promise<void>}
 */
export function initAudio() {
  if (preloadPromise) return preloadPromise;
  const ctx = getAudioContext();
  if (!ctx) return Promise.resolve();

  const names = ['click', 'game-over', 'new-scenario'];
  preloadPromise = Promise.all(
    names.map(async (basename) => {
      for (const ext of EXTENSIONS) {
        const url = `${BASE}/${basename}${ext}`;
        try {
          const res = await fetch(url);
          if (!res.ok) continue;
          const arrayBuffer = await res.arrayBuffer();
          const buf = await ctx.decodeAudioData(arrayBuffer);
          buffers[basename] = buf;
          return;
        } catch (_) {}
      }
    })
  ).then(() => {
    if (ctx.state === 'suspended') ctx.resume();
  });
  return preloadPromise;
}

function playFile(basename, fallbackFrequency = 800) {
  if (getMuted()) return;
  const buf = buffers[basename];
  if (buf) {
    playFromBuffer(buf);
    return;
  }
  playFallbackBeep(fallbackFrequency);
}

/** Klick-Sound: dezent per Web-Audio-Beep (vorheriger, unauffälliger Sound). */
export function playClick() {
  if (getMuted()) return;
  playFallbackBeep(800);
}

export function playGameOver() {
  playFile('game-over', 400);
}

export function playNewScenario() {
  playFile('new-scenario', 600);
}

// --- Hintergrundmusik pro Phase (Pool pro Phase, zufällige Auswahl, nächster Track nach Ende) ---
let bgmEl = null;
let currentBGMPhase = null;

/**
 * Wählt zufällig einen Track aus dem Phasen-Pool (je Phase eigener Pool, Auswahl zufällig).
 */
function pickFromPool(phase) {
  const pool = BGM_POOL_BY_PHASE[phase];
  if (!pool || pool.length === 0) return null;
  return pool[Math.floor(Math.random() * pool.length)];
}

function playNextTrackInPhase() {
  if (getMusicMuted() || currentBGMPhase == null || !bgmEl) return;
  const filename = pickFromPool(currentBGMPhase);
  if (!filename) return;
  bgmEl.volume = getMusicVolume();
  bgmEl.src = `${BGM_BASE}/${filename}`;
  bgmEl.play().catch(() => {});
}

function onBGMEnded() {
  playNextTrackInPhase();
}

/**
 * Startet oder wechselt die BGM zur angegebenen Spielphase (1–3).
 * Wählt zufällig einen Track aus dem Phasen-Pool; nach Ende des Tracks folgt der nächste (zufällig aus demselben Pool).
 * Respektiert Musik an/aus und Lautstärke (getMusicMuted, getMusicVolume).
 */
export function playBGMForPhase(phase) {
  if (getMusicMuted()) return;
  const filename = pickFromPool(phase);
  if (!filename) return;
  if (!bgmEl) {
    bgmEl = new Audio();
    bgmEl.loop = false;
    bgmEl.addEventListener('ended', onBGMEnded);
  }
  bgmEl.volume = getMusicVolume();
  if (currentBGMPhase === phase && bgmEl.src && bgmEl.src.endsWith(filename)) {
    bgmEl.play().catch(() => {});
    return;
  }
  currentBGMPhase = phase;
  bgmEl.src = `${BGM_BASE}/${filename}`;
  bgmEl.play().catch(() => {});
}

/** BGM wieder starten (z. B. nach „Musik an“), wenn eine Phase aktiv war. */
export function resumeBGM() {
  if (getMusicMuted() || currentBGMPhase == null) return;
  if (bgmEl && bgmEl.src) {
    bgmEl.volume = getMusicVolume();
    bgmEl.play().catch(() => {});
  }
}

/** Startet BGM für Phase 1 (z. B. beim Spielstart). */
export function playBGM() {
  playBGMForPhase(1);
}

export function stopBGM() {
  if (bgmEl) {
    bgmEl.pause();
    bgmEl.currentTime = 0;
  }
  currentBGMPhase = null;
}

export { getMuted };
